/**
 * EditorialDatumStrip — K2. The dotted "datum-strip" horizontal divider the
 * reference redesign (`~/Desktop/Redhead_BTM_Feasibility_Redesign_1.pdf`,
 * "Reactor Datum") uses to separate bands within a page WITHOUT a heavy solid
 * rule or a fat white gap. A single token-styled dotted line, optionally
 * carrying a small-caps mono LABEL inset on the left (an engineering
 * "datum" tag) and/or a right-aligned VALUE.
 *
 * THEME-AWARE: the rule colour reads `roleColor(theme, 'hair' | 'rule')` and
 * the label/value inks read `roleColor`, so the strip follows the active
 * preset in every theme — no literal slate/blue. The dotted-border STYLE comes
 * from the shared {@link datumStripBorderStyle} spacing token so the strip and
 * any other datum-strip surface can never drift.
 *
 *   - role='rule'  (default) → stronger band/section separator.
 *   - role='hair'            → faint inter-block divider.
 *
 * Pure / synchronous / print + reduced-motion friendly (no animation, no
 * shadow), identical on-screen and in viewMode='export'. The vertical
 * breathing room around the strip is the CALLER's (compose with the
 * reportSpacingTokens section/flow gaps) so this primitive styles only the
 * rule + its inset labels.
 */
import React from 'react';
import type { ReportTheme } from '../../../../types/reportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { datumStripBorderStyle } from '../../../../lib/reportSpacingTokens';
import { REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';

const MONO = 'var(--report-body-font, inherit)';

export interface EditorialDatumStripProps {
  theme: ReportTheme;
  /**
   * Divider strength. `'rule'` (default) = stronger band/section separator;
   * `'hair'` = faint inter-block divider.
   */
  role?: 'hair' | 'rule';
  /** Optional small-caps mono label inset on the left (an engineering datum tag). */
  label?: React.ReactNode;
  /** Optional right-aligned value/figure paired with the label. */
  value?: React.ReactNode;
  /** Use accent ink for the label instead of muted ink (default muted). */
  accentLabel?: boolean;
  /** Dotted-line thickness in px (default 1). */
  weight?: number;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * The dotted datum-strip divider. When no label/value is supplied it renders a
 * bare dotted rule; with a label it renders the label (and optional value) sat
 * ON the rule, lifted up so the dotted line reads as the datum baseline.
 */
export function EditorialDatumStrip({
  theme,
  role = 'rule',
  label,
  value,
  accentLabel = false,
  weight = 1,
  id,
  className,
  style,
}: EditorialDatumStripProps): React.ReactElement {
  const border = datumStripBorderStyle(theme, role, weight);
  const labelInk = roleColor(theme, accentLabel ? 'accent' : 'muted');
  const valueInk = roleColor(theme, 'ink');

  const rootStyle: React.CSSProperties = {
    ...border,
    paddingTop: 6,
    ...style,
  };

  // Bare rule — no label/value.
  if (label == null && value == null) {
    return <div id={id} className={className} style={rootStyle} aria-hidden="true" />;
  }

  const labelStyle: React.CSSProperties = {
    fontFamily: MONO,
    fontSize: REPORT_TEXT_PX.label,
    fontWeight: 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: labelInk,
  };
  const valueStyle: React.CSSProperties = {
    fontFamily: MONO,
    fontSize: REPORT_TEXT_PX.label,
    fontWeight: 600,
    letterSpacing: '0.04em',
    color: valueInk,
  };

  return (
    <div
      id={id}
      className={className}
      style={{ ...rootStyle, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}
    >
      {label != null ? <span style={labelStyle}>{label}</span> : <span />}
      {value != null ? <span style={valueStyle}>{value}</span> : null}
    </div>
  );
}
